import { useCallback, useEffect, useState } from "react"
import type { AppShell } from "../App"
import { closeCycle, loadOpenCycles, type OpenCycle, type OpenCyclesResponse } from "../lib/api"
import { Button } from "../components/ui/button"
import { cn } from "../lib/utils"

/**
 * Ciclos SLA por fechar.
 *
 * O fecho deixou de ser automático: congelar a janela errada — num ciclo
 * bimestral, ou com a taxa por definir — só dá erro semanas depois, ao
 * facturar. Aqui vêem-se os números antes de decidir.
 */

const STATE_META: Record<OpenCycle["state"], { label: string; cls: string }> = {
  ready: { label: "Pronto a fechar", cls: "border-emerald-500 text-emerald-600 dark:text-emerald-400" },
  blocked: { label: "Bloqueado", cls: "border-destructive text-destructive" },
  running: { label: "A decorrer", cls: "border-muted-foreground/40 text-muted-foreground" },
}

function euros(value: number | null): string {
  return value === null ? "—" : `€${value.toLocaleString("pt-PT", { maximumFractionDigits: 2 })}`
}

export function OpenCycles({ shell }: { shell: AppShell }) {
  const [data, setData] = useState<OpenCyclesResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [includeEmpty, setIncludeEmpty] = useState(false)

  const refresh = useCallback(() => {
    loadOpenCycles(shell.client, includeEmpty)
      .then((res) => {
        setData(res)
        setError(null)
      })
      .catch((err) => setError((err as Error).message))
  }, [shell.client, includeEmpty])

  useEffect(refresh, [refresh])

  const close = async (cycle: OpenCycle) => {
    setBusy(cycle.contract_id)
    try {
      await closeCycle(shell.client, cycle.contract_id, cycle.window_from, cycle.window_to)
      shell.bridge.toast({
        variant: "success",
        title: "Ciclo fechado",
        description: `${cycle.entity_name ?? cycle.contract_title} · já aparece em Por faturar.`,
      })
      refresh()
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        title: "Não foi possível fechar",
        description: (err as Error).message,
      })
    } finally {
      setBusy(null)
    }
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-lg font-semibold text-destructive">Não foi possível ler os ciclos</h1>
        <p className="mt-1 text-sm text-muted-foreground">{error}</p>
      </div>
    )
  }
  if (!data) return <div className="p-6 text-sm text-muted-foreground">A calcular ciclos…</div>

  const { totals } = data

  return (
    <div className="mx-auto max-w-[1200px] px-6 pb-16">
      <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-md border bg-border sm:grid-cols-4">
        <Kpi value={String(totals.ready)} label="Prontos a fechar" sub="janela terminada" />
        <Kpi value={euros(totals.ready_amount)} label="Valor estimado" sub="dos que estão prontos" />
        <Kpi value={String(totals.blocked)} label="Bloqueados" sub="falta taxa ou horas" />
        <Kpi value={String(totals.running)} label="A decorrer" sub="janela ainda aberta" />
      </div>

      {totals.empty > 0 && (
        <p className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-dashed px-4 py-2 text-sm text-muted-foreground">
          <b className="font-mono text-foreground">{totals.empty}</b>
          contrato{totals.empty === 1 ? "" : "s"} com ciclo aberto e{" "}
          <b>sem horas registadas</b> nesta janela — fora da lista, porque não há nada para fechar.
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto h-7 px-2 text-xs"
            onClick={() => setIncludeEmpty((v) => !v)}
          >
            {includeEmpty ? "Esconder" : "Mostrar"}
          </Button>
        </p>
      )}

      <div className="mb-3 mt-9 flex flex-wrap items-baseline gap-3 border-b pb-2">
        <h2 className="text-base font-semibold">Ciclos por fechar</h2>
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {data.cycles.length} contratos
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          Fechar congela a janela, as horas e a taxa. Confirma antes.
        </span>
      </div>

      {data.cycles.length === 0 ? (
        <p className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhum contrato com ciclo recorrente por fechar.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {data.cycles.map((cycle) => {
            const meta = STATE_META[cycle.state]
            return (
              <article
                key={cycle.contract_id}
                className={cn(
                  "rounded-md border bg-card px-4 py-3",
                  cycle.state === "ready" && "border-emerald-500/40",
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <b className="text-sm">{cycle.entity_name ?? "—"}</b>
                  <span className="text-sm text-muted-foreground">{cycle.contract_title}</span>
                  <span
                    className={cn(
                      "rounded border px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider",
                      meta.cls,
                    )}
                  >
                    {meta.label}
                  </span>
                  {cycle.cycle_months ? (
                    <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      ciclo de {cycle.cycle_months} {cycle.cycle_months === 1 ? "mês" : "meses"}
                    </span>
                  ) : null}
                </div>

                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 font-mono text-xs text-muted-foreground">
                  <span>
                    {cycle.window_from} → {cycle.window_to}
                  </span>
                  <span>
                    {cycle.days_to_close < 0
                      ? `terminou há ${Math.abs(cycle.days_to_close)} d`
                      : `fecha em ${cycle.days_to_close} d`}
                  </span>
                  <span>
                    <b className="text-foreground">{cycle.hours_consumed}</b> h consumidas
                    {cycle.hours_included ? ` · ${cycle.hours_included} incluídas` : ""}
                  </span>
                  <span>
                    <b className="text-foreground">{cycle.billable_hours}</b> h faturáveis
                  </span>
                  {cycle.effective_hourly_rate !== null && (
                    <span>{euros(cycle.effective_hourly_rate)}/h</span>
                  )}
                  {cycle.estimated_amount !== null && (
                    <span className="font-semibold text-foreground">
                      ≈ {euros(cycle.estimated_amount)}
                    </span>
                  )}
                </div>

                {cycle.blockers.length > 0 && (
                  <ul className="mt-2 border-l-2 border-destructive bg-destructive/10 px-3 py-2 text-sm">
                    {cycle.blockers.map((blocker) => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    disabled={cycle.state !== "ready" || busy === cycle.contract_id}
                    title={
                      cycle.state === "running"
                        ? "A janela ainda não terminou"
                        : cycle.state === "blocked"
                          ? "Resolve o que falta antes de fechar"
                          : "Fechar o ciclo nesta janela"
                    }
                    onClick={() => close(cycle)}
                  >
                    {busy === cycle.contract_id ? "A fechar…" : "Fechar ciclo"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      shell.bridge.navigateCrm({ href: `/contracts/${cycle.contract_id}` })
                    }
                  >
                    Contrato →
                  </Button>
                </div>
              </article>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Kpi({ value, label, sub }: { value: string; label: string; sub: string }) {
  return (
    <div className="bg-card px-4 py-3">
      <div className="font-mono text-xl font-semibold leading-none">{value}</div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-[11px] text-muted-foreground">{sub}</div>
    </div>
  )
}
